import React, { useState, useEffect, useRef, useCallback } from 'react'
import classNames from 'classnames'
import ReactCSSTransitionGroup from 'react-addons-css-transition-group'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import { upperFirst } from 'lodash'

import { useCallbackOnEsc } from 'hooks/useCallbackOnEsc'
import { useIsTimestampExpired } from 'hooks/useIsTimestampExpired'
import { useMetricsLog } from 'helpers/uilogging/hooks'
import { createPartnerID } from 'helpers/uilogging/helpers'
import { getParsedStorageItem, saveToStorage } from 'helpers/playersMatching'
import { Actions, FEATURE, Items } from './logConstants'

import {
    openExternalBrowser,
    openHangar,
    Button,
    EmptyList,
    Tooltip,
} from 'react-wot-bootstrap'

import { openInvitations } from 'state/actions/app'
import { openBattleRooms } from 'state/actions/unit_create'
import requestAccountInfo from 'state/actions/account'
import { clearAccountStatus } from 'state/actions/recommended_clans'
import { requestGlobalMapInfo } from 'state/actions/global_map'
import { requestStrongholdManeuversInfo } from 'state/actions/stronghold_maneuvers'
import { requestClanSupplyProgressionSettingsInfo } from 'state/actions/clan_supply_progression_settings'
import { requestClanSupplyQuestsInfo } from 'state/actions/clan_supply_quests'
import { requestCraftMachineInfo } from 'state/actions/craft_machine'
import { requestClanInvites, clanInvitesChecked } from 'state/actions/clan_invites'
import { requestServerTimestamp } from 'state/actions/client'

import ButtonSimple from 'components/ButtonSimple/ButtonSimple'
import LoadingScreen from 'components/LoadingScreen/LoadingScreen'

import LandingBonus from './components/LandingBonus/LandingBonus'
import LandingClans from './components/LandingClans/LandingClans'
import LandingPromo from './components/LandingPromo/LandingPromo'
import LandingResult from './components/LandingResult/LandingResult'
import LandingToursOfDuty from './components/LandingToursOfDuty/LandingToursOfDuty'
import NextSliderButton from './components/NextSliderButton/NextSliderButton'
import PrevSliderButton from './components/PrevSliderButton/PrevSliderButton'
import LandingStronghold from './components/LandingStronghold/LandingStronghold'
import LandingGlobalMap from './components/LandingGlobalMap/LandingGlobalMap'
import LogWrapper from './components/LogWrapper/LogWrapper'

import {
    sendEnterLandingSoundState,
    sendEnterGlobalMapSoundState,
    playEnterGlobalMapSound,
} from 'state/actions/sounds'

import { LOCAL_STORAGE_KEYS } from 'constants'
import {
    WGCWX_SHOP,
    WGCWX_SHOP_REWARDS,
    WGCWX_BATTLE_SCHEDULE,
    WGCWX_LEADERBOARDS,
} from 'urls'

import { openClanSupplyProgression, openClanSupplyQuests } from '../../helpers/web_to_client'

import {
    AccountStatusPropTypes,
    JoinResultPropTypes,
} from './propTypes'

import {
    LANDING_GOTO_BATTLE,
    LANDING_GOTO_BATTLE_TOOLTIP,
    LANDING_GOTO_CREATE_CLAN,
    LANDING_GOTO_CREATE_CLAN_TOOLTIP,
    LANDING_GOTO_INVITATIONS,
    LANDING_GOTO_INVITATIONS_TOOLTIP,
    LANDING_GOTO_GLOBAL_MAP_EVENT_TOOLTIP,
    LANDING_GOTO_MANEUVERS_EVENT,
    LANDING_GOTO_MANEUVERS_EVENT_TOOLTIP,
    LANDING_GOTO_CLAN_SUPPLY,
    LANDING_GOTO_CLAN_SUPPLY_TOOLTIP,
    LANDING_GOTO_CLAN_SUPPLY_IS_DISABLED_TOOLTIP,
} from './translations'

import {
    LANDING_CLANS_TABLE_ERROR_BUTTON,
    LANDING_CLANS_TABLE_ERROR_MESSAGE,
    LANDING_CLANS_TABLE_ERROR_TITLE,
} from 'containers/Landing/components/LandingClans/constants'

import styles from './Landing.scss'

const COMPONENTS = {
    landingPromo: LandingPromo,
    landingBonus: LandingBonus,
    landingToursOfDuty: LandingToursOfDuty,
    landingClans: LandingClans,
    landingStronghold: LandingStronghold,
    landingGlobalMap: LandingGlobalMap,
}
const FOOTER_ANIMATION_TIMEOUT = 500
const SLIDE_BG_ANIMATION_TIMEOUT = 300
const SLIDE_ANIMATION_TIMEOUT = 500
const TAB_KEY_CODE = 9

export const Landing = (props) => {
    const slidesCount = props.clanSlides.length
    const checkLastSlideToShow = () => getParsedStorageItem(LOCAL_STORAGE_KEYS.LANDING_CLAN_SELECTION_SHOWN) ?
            slidesCount - 1 : 0
    const [nextSlide, setNextSlide] = useState(checkLastSlideToShow())
    const [currentSlide, setCurrentSlide] = useState(checkLastSlideToShow())
    const [sliderItemWidth, setSliderItemWidth] = useState(window.innerWidth)
    const [sliderItemHeight, setSliderItemHeight] = useState(window.innerHeight)
    const [partnerID] = useState(createPartnerID())
    const slider = useRef(null)
    const currentSlideRef = useRef(0)
    const isVisibleStartExpired = useIsTimestampExpired(props.maneuversVisibleStartDate || 0)
    const isVisibleEndExpired = useIsTimestampExpired(props.maneuversVisibleEndDate || 0)

    useEffect(() => {
        const handleWindowResize = () => {
            setSliderItemWidth(window.innerWidth)
            setSliderItemHeight(window.innerHeight)
        }

        const handleKeyDown = (e) => {
            switch (e.keyCode) {
            case TAB_KEY_CODE:
                e.preventDefault()
                break
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('resize', handleWindowResize)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('resize', handleWindowResize)
        }
    },[sliderItemWidth, sliderItemHeight])

    useEffect(() => {
        props.requestServerTimestamp()
        props.requestGlobalMapInfo()
        props.requestCraftMachineInfo()
        props.requestClanInvites()
        props.sendEnterLandingSoundState()
        props.requestStrongholdManeuversInfo()
        props.requestClanSupplyProgressionSettingsInfo()
        props.requestClanSupplyQuestsInfo()
    },[])

    useEffect(() => {
        currentSlideRef.current = currentSlide
        if (isLastSlide) {
            saveToStorage(LOCAL_STORAGE_KEYS.LANDING_CLAN_SELECTION_SHOWN, true)
        }
    }, [currentSlide])

    const log = useMetricsLog(FEATURE)

    const handleLandingClose = useCallback(() => {
        const slideName = upperFirst(props.clanSlides[currentSlideRef.current])
        log({
            action: Actions.Close,
            item: Items[slideName],
            parentScreen: Items.Hangar,
            partnerID: partnerID,
        })
    }, [log])

    const handleButtonClick = useCallback((item) => {
        log({
            action: Actions.Click,
            item: item,
            parentScreen: Items.Hangar,
            partnerID: partnerID,
        })
    }, [log])

    useCallbackOnEsc(() => {
        handleLandingClose()
        openHangar()
    })

    const handleCreateClanClick = () => {
        handleButtonClick(Items.CreateClanButton)
        openExternalBrowser(props.clanCreateUrl)
    }

    const handleInvitationsClick = () => {
        handleButtonClick(Items.ClanInvitationsButton)
        props.openInvitations()
        props.clanInvitesChecked()
    }

    const handleOpenBattleRoomsClick = () => {
        handleButtonClick(Items.LegionnaireBattleButton)
        props.openBattleRooms()
    }

    const handleGoToRecommendedClansClick = () => {
        if (slider.current) {
            slider.current.slickGoTo(slidesCount - 1, true)
        }
    }

    const handleGoToFirstSlideClick = () => {
        if (slider.current) {
            slider.current.slickGoTo(0, true)
        }
    }

    const handleLandingResultClick = () => {
        if (!props.accountStatus.isAccountInClan && !props.joinResult.currentClan) {
            openHangar()
        } else {
            props.fetchAccountInfo()
        }
    }

    const handleSliderBeforeChange = (oldSlide, nextSlide) => {
        setTimeout(() => setNextSlide(nextSlide), 0)
        setTimeout(() => setCurrentSlide(nextSlide), SLIDE_ANIMATION_TIMEOUT)
    }

    const isLastSlide = nextSlide === slidesCount - 1

    const handleGoToClanSupplyClick = () => {
        if (props.isClanSupplyProgressionEnabled) {
            openClanSupplyProgression()
        } else if (props.areClanSupplyQuestsEnabled) {
            openClanSupplyQuests()
        }
    }

    const renderFooter = () => {
        const {
            isCraftServiceEnabled, personalBonusesEnabled,
            eventRewardsEnabled,
        } = props
        const personalShopEnabled = (personalBonusesEnabled || eventRewardsEnabled)
        const isManeuversEventVisible = isVisibleStartExpired && !isVisibleEndExpired

        const handleClickEvent = () => {
            handleButtonClick(Items.GlobalMapEventButton)

            if (isCraftServiceEnabled) {
                window.location = props.craftMachineUrl
                return
            }
            if (personalShopEnabled) {
                props.sendEnterGlobalMapSoundState()
                props.playEnterGlobalMapSound()

                if (eventRewardsEnabled) {
                    props.openEventRewards()
                    return
                }
                if (personalBonusesEnabled) {
                    props.openPersonalShop()
                    return
                }
            }
        }

        const handleClickEventManeuvers = () => {
            const url = new URL(props.wgshEventsUrl)
            const urlParams = new URLSearchParams(url.search)
            urlParams.append('isAutoRedirect', 'true')
            url.search = urlParams.toString()
            window.location = url.toString()
        }

        return (
            <ReactCSSTransitionGroup
                transitionName="footerAnimation"
                transitionEnterTimeout={FOOTER_ANIMATION_TIMEOUT}
                transitionLeaveTimeout={FOOTER_ANIMATION_TIMEOUT}
            >
                {!isLastSlide && (
                    <div className={styles.footer}>
                        <div className={styles.footerContainer}>
                            <div>
                                {(personalShopEnabled || isCraftServiceEnabled) && props.eventName && (
                                    <div className={styles.button}>
                                        <Button tooltipId="goto_personal_bonuses" onClick={handleClickEvent}>
                                            {props.eventName}
                                        </Button>
                                        <Tooltip id="goto_personal_bonuses">
                                            {LANDING_GOTO_GLOBAL_MAP_EVENT_TOOLTIP}
                                        </Tooltip>
                                    </div>
                                )}

                                {isManeuversEventVisible && (
                                    <div className={styles.button}>
                                        <Button tooltipId="goto_maneuvers" onClick={handleClickEventManeuvers}>
                                            {LANDING_GOTO_MANEUVERS_EVENT}
                                        </Button>
                                        <Tooltip id="goto_maneuvers">
                                            {LANDING_GOTO_MANEUVERS_EVENT_TOOLTIP}
                                        </Tooltip>
                                    </div>
                                )}

                                <div className={styles.button}>
                                    <Button
                                        type="secondary"
                                        tooltipId="goto_clan_supply"
                                        onClick={handleGoToClanSupplyClick}
                                        isDisabled={!props.isClanSupplyProgressionEnabled &&
                                        !props.areClanSupplyQuestsEnabled}
                                    >
                                        {LANDING_GOTO_CLAN_SUPPLY}
                                    </Button>
                                    <Tooltip id="goto_clan_supply">
                                        {props.isClanSupplyProgressionEnabled || props.areClanSupplyQuestsEnabled ?
                                            LANDING_GOTO_CLAN_SUPPLY_TOOLTIP :
                                            LANDING_GOTO_CLAN_SUPPLY_IS_DISABLED_TOOLTIP
                                        }
                                    </Tooltip>
                                </div>
                            </div>

                            <div>
                                {renderLegionnaireButton()}
                                <div className={styles.button}>
                                    <ButtonSimple
                                        tooltipId="goto_invitations_tooltip"
                                        onClick={handleInvitationsClick}
                                        messages={props.clanInvitesTotal}
                                    >
                                        {LANDING_GOTO_INVITATIONS}
                                    </ButtonSimple>
                                    <Tooltip id="goto_invitations_tooltip">{LANDING_GOTO_INVITATIONS_TOOLTIP}</Tooltip>
                                </div>
                                <div className={styles.button}>
                                    <ButtonSimple
                                        isExternal
                                        tooltipId="goto_create_clan_tooltip"
                                        onClick={handleCreateClanClick}
                                    >
                                        {LANDING_GOTO_CREATE_CLAN}
                                    </ButtonSimple>
                                    <Tooltip id="goto_create_clan_tooltip">{LANDING_GOTO_CREATE_CLAN_TOOLTIP}</Tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ReactCSSTransitionGroup>
        )
    }

    const renderLegionnaireButton = () => {
        return (
            <div className={styles.button}>
                <ButtonSimple
                    tooltipId="goto_battle_tooltip"
                    onClick={handleOpenBattleRoomsClick}
                >
                    {LANDING_GOTO_BATTLE}
                </ButtonSimple>
                <Tooltip id="goto_battle_tooltip">
                    {LANDING_GOTO_BATTLE_TOOLTIP}
                </Tooltip>
            </div>
        )
    }

    const renderSliderBackground = () => {
        const { clanSlides } = props

        const slideBgBattlesClassNames = classNames(
            styles[`${clanSlides[nextSlide]}`], {
                [styles.isCNRealm]: props.isCNRealm,
            })

        const slidesBg = (
            <div
                className={slideBgBattlesClassNames}
                key={`slideBg_${clanSlides[nextSlide]}`}
            />
        )

        return (
            <ReactCSSTransitionGroup
                transitionName="slideBackground"
                transitionEnterTimeout={SLIDE_BG_ANIMATION_TIMEOUT}
                transitionLeaveTimeout={SLIDE_BG_ANIMATION_TIMEOUT}
            >
                {slidesBg}
            </ReactCSSTransitionGroup>
        )
    }

    const renderSlider = () => {
        const { clanSlides } = props

        const style = {
            width: sliderItemWidth,
            height: sliderItemHeight,
        }

        const slides = clanSlides.map((slideName, i) => {
            const ComponentName = COMPONENTS[slideName]
            return (
                <div key={i}>
                    <div className={styles.item} style={style}>
                        <LogWrapper
                            WrappedComponent={ComponentName}
                            partnerID={partnerID}
                            isCurrent={currentSlide === i}
                            slideName={slideName}
                            isLastSlide={isLastSlide}
                            onLastSlideBackClick={handleGoToFirstSlideClick}
                            onGoToRecommendedClansButtonClick={handleGoToRecommendedClansClick}
                        />
                    </div>
                </div>
            )
        })

        return (
            <Slider
                accessibility={false}
                arrows={!isLastSlide}
                speed={SLIDE_ANIMATION_TIMEOUT}
                dots={!isLastSlide}
                draggable={false}
                infinite={false}
                initialSlide={currentSlide}
                nextArrow={<NextSliderButton/>}
                prevArrow={<PrevSliderButton/>}
                ref={slider}
                beforeChange={handleSliderBeforeChange}
            >
                {slides}
            </Slider>
        )
    }

    if (props.accountStatus.hasError) {
        return (
            <div className={styles.emptyListContainer}>
                <EmptyList
                    title={LANDING_CLANS_TABLE_ERROR_TITLE}
                    message={LANDING_CLANS_TABLE_ERROR_MESSAGE}
                    buttonCaption={LANDING_CLANS_TABLE_ERROR_BUTTON}
                    onButtonClick={props.refreshAccountStatus}
                />
            </div>
        )
    }

    if (props.accountStatus.isFetchingCompleted && props.joinResult.joinedClan) {
        return (
            <LandingResult
                isWaiting={!props.accountStatus.isAccountInClan}
                onClick={handleLandingResultClick}
            />
        )
    }

    if (props.joinResult.currentClan) {
        return (
            <LandingResult
                isWaiting={false}
                onClick={handleLandingResultClick}
            />
        )
    }

    if (
        props.accountStatus.loading ||
        props.globalMapLoading
    ) {
        return (
            <LoadingScreen />
        )
    }

    return (
        <div className={styles.base}>
            <div className={styles.body}>
                {renderSliderBackground()}
                {renderSlider()}
                {renderFooter()}
            </div>
        </div>
    )
}

const mapStateToProps = ({
    craftMachine, recommendedClans, globalMap,
    settings, clanInvites, strongholdManeuvers, clanSupplyProgressionSettings,
    clanSupplyQuests,
}) => ({
    accountStatus: recommendedClans.accountStatus,
    isCraftServiceEnabled: craftMachine.is_craft_service_enabled,
    craftMachineUrl: settings.craftMachineUrl,
    wgshEventsUrl: settings.wgshEventsUrl,
    eventName: globalMap.context_id,
    joinResult: recommendedClans.joinResult,
    personalBonusesEnabled: globalMap.personal_bonuses_enabled,
    eventRewardsEnabled: globalMap.event_rewards_enabled,
    globalMapLoading: globalMap.loading,
    isCNRealm: settings.isCNRealm,
    clanCreateUrl: settings.clanCreateUrl,
    clanSlides: settings.clanSlides,
    clanInvitesTotal: clanInvites.total,
    maneuversVisibleStartDate: strongholdManeuvers.visibleStartDate,
    maneuversVisibleEndDate: strongholdManeuvers.visibleEndDate,
    isClanSupplyProgressionEnabled: clanSupplyProgressionSettings.enabled,
    areClanSupplyQuestsEnabled: clanSupplyQuests.enabled,
})

const mapDispatchToProps = (dispatch) => ({
    openBattleRooms: () => {
        dispatch(openBattleRooms())
    },
    refreshAccountStatus: () => {
        dispatch(clearAccountStatus())
        dispatch(requestAccountInfo())
    },
    fetchAccountInfo: () => {
        dispatch(requestAccountInfo())
    },
    openInvitations: () => dispatch(openInvitations()),
    clanInvitesChecked: () => dispatch(clanInvitesChecked()),
    openPersonalShop: () => dispatch(push(WGCWX_SHOP)),
    openEventRewards: () => dispatch(push(WGCWX_SHOP_REWARDS)),
    openBattleSchedule: () => dispatch(push(WGCWX_BATTLE_SCHEDULE)),
    openLeaderboards: () => dispatch(push(WGCWX_LEADERBOARDS)),
    requestGlobalMapInfo: () => dispatch(requestGlobalMapInfo()),
    requestClanInvites: () => dispatch(requestClanInvites()),
    requestCraftMachineInfo: () => dispatch(requestCraftMachineInfo()),
    sendEnterLandingSoundState: () => dispatch(sendEnterLandingSoundState()),
    sendEnterGlobalMapSoundState: () => dispatch(sendEnterGlobalMapSoundState()),
    playEnterGlobalMapSound: () => dispatch(playEnterGlobalMapSound()),
    requestStrongholdManeuversInfo: () => dispatch(requestStrongholdManeuversInfo()),
    requestServerTimestamp: () => dispatch(requestServerTimestamp()),
    requestClanSupplyProgressionSettingsInfo: () => dispatch(requestClanSupplyProgressionSettingsInfo()),
    requestClanSupplyQuestsInfo: () => dispatch(requestClanSupplyQuestsInfo()),
})

Landing.propTypes = {
    accountStatus: AccountStatusPropTypes.isRequired,
    isCraftServiceEnabled: PropTypes.bool,
    clanCreateUrl: PropTypes.string.isRequired,
    craftMachineUrl: PropTypes.string,
    wgshEventsUrl: PropTypes.string,
    eventName: PropTypes.string,
    joinResult: JoinResultPropTypes.isRequired,
    personalBonusesEnabled: PropTypes.bool,
    eventRewardsEnabled: PropTypes.bool,
    globalMapLoading: PropTypes.bool,
    isCNRealm: PropTypes.bool,
    clanSlides: PropTypes.array.isRequired,
    clanInvitesTotal: PropTypes.number,

    openBattleRooms: PropTypes.func.isRequired,
    openInvitations: PropTypes.func.isRequired,
    clanInvitesChecked: PropTypes.func.isRequired,
    openPersonalShop: PropTypes.func.isRequired,
    openEventRewards: PropTypes.func.isRequired,
    refreshAccountStatus: PropTypes.func.isRequired,
    fetchAccountInfo: PropTypes.func.isRequired,
    requestGlobalMapInfo: PropTypes.func.isRequired,
    requestCraftMachineInfo: PropTypes.func.isRequired,
    requestClanInvites: PropTypes.func.isRequired,
    sendEnterLandingSoundState: PropTypes.func.isRequired,
    sendEnterGlobalMapSoundState: PropTypes.func.isRequired,
    playEnterGlobalMapSound: PropTypes.func.isRequired,
    requestStrongholdManeuversInfo: PropTypes.func.isRequired,
    maneuversVisibleStartDate: PropTypes.number,
    maneuversVisibleEndDate: PropTypes.number,
    requestServerTimestamp: PropTypes.func.isRequired,
    requestClanSupplyProgressionSettingsInfo: PropTypes.func.isRequired,
    isClanSupplyProgressionEnabled: PropTypes.bool,
    requestClanSupplyQuestsInfo: PropTypes.func.isRequired,
    areClanSupplyQuestsEnabled: PropTypes.bool,
}

export default connect(mapStateToProps, mapDispatchToProps)(Landing)



// WEBPACK FOOTER //
// ./containers/Landing/Landing.jsx